import { useMemo } from "react";
import { ComponentType } from "react";
import { AppConfig } from "../interfaces/appIntegration.interfaces";
import { InstallationComponentId } from "../enum/appInstalationComponent.enum";
import { InstallationComponentProps } from "../interfaces/appInstallation.interface";
import { EcommercePaypalButtonSetup, EcommerceStripeButtonSetup, VrAssistantSetup } from "../components/setup";

const INSTALLATION_COMPONENTS: Record<InstallationComponentId, ComponentType<InstallationComponentProps>> = {
  [InstallationComponentId.ECOMMERCE_PAYPAL_SETUP]: EcommercePaypalButtonSetup,
  [InstallationComponentId.VIRTUAL_ASSISTANT_SETUP]: VrAssistantSetup,
  [InstallationComponentId.ECOMMERCE_STRIPE_SETUP]: EcommerceStripeButtonSetup,
};

interface UseInstallationComponentResult {
  Component: ComponentType<InstallationComponentProps> | null;
  isValid: boolean;
  error: string | null;
}

export const useInstallationComponent = (app: AppConfig | null): UseInstallationComponentResult => {
  return useMemo(() => {
    if (!app) {
      return {
        Component: null,
        isValid: false,
        error: "App not provided",
      };
    }

    if (app.installation.type !== "component") {
      return {
        Component: null,
        isValid: false,
        error: `Installation type "${app.installation.type}" does not support components`,
      };
    }

    if (!app.installation.componentId) {
      return {
        Component: null,
        isValid: false,
        error: `App "${app.id}" has no componentId defined`,
      };
    }

    const Component = INSTALLATION_COMPONENTS[app.installation.componentId];

    if (!Component) {
      return {
        Component: null,
        isValid: false,
        error: `Component "${app.installation.componentId}" not found in registry`,
      };
    }

    return {
      Component,
      isValid: true,
      error: null,
    };
  }, [app]);
};

export const useInstallationComponentOrNull = (app: AppConfig | null): ComponentType<InstallationComponentProps> | null => {
  const { Component } = useInstallationComponent(app);
  return Component;
};
